import * as React from 'react';
import { Button, ButtonGroup, cn, TextArea } from '@heroui/react';
import { IconChevronLeft, IconPhotoPlus } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { CroppedImageView } from '@/components/RichTextEditor/interaction/floating/image/CroppedImageView';
import { RichTextEditor } from '@/components/RichTextEditor/RichTextEditor';
import { SelectMyImage, type SelectMyImagePick } from '@/components/SelectMyImage';
import { DocToolBar } from './DocToolBar';
import type { KnowledgeBaseMetaBackground, KnowledgeBaseMetaHeaderImage } from './knowledge-base-meta';
import {
  KNOWLEDGE_BASE_HEADER_IMAGE_DISPLAY_ASPECT_RATIO,
  metaBackgroundToMiddleColumnStyle,
  parseKnowledgeBaseMetaBackgroundFromMeta,
  resolveKnowledgeBaseHeaderImageFromRecord,
} from './knowledge-base-meta';

/** 与 `getLexicalTheme` 中 h1 一致：知识库文档标题输入/只读展示 */
export const DOC_TITLE_INPUT_CLASSNAME = cn(
  'w-full min-w-0 text-4xl font-bold leading-tight',
  'whitespace-normal break-words [overflow-wrap:anywhere]',
  'rounded-none border-0 border-b border-divider bg-transparent px-0 py-2 shadow-none h-auto min-h-0',
  'focus-visible:ring-0 focus-visible:border-ring',
);

function DocTitleTextArea(props: React.ComponentProps<typeof TextArea>) {
  const { className, value, onChange, ...rest } = props;
  const ref = React.useRef<HTMLTextAreaElement>(null);

  const adjustHeight = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  React.useLayoutEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  return (
    <TextArea
      {...rest}
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => {
        onChange?.(e);
        requestAnimationFrame(adjustHeight);
      }}
      className={cn(DOC_TITLE_INPUT_CLASSNAME, 'resize-none overflow-hidden align-top', className)}
    />
  );
}

export type DocRichTextDocumentProps = {
  documentBackground: KnowledgeBaseMetaBackground | null;
  headerImage: KnowledgeBaseMetaHeaderImage | null;
  /** 只读展示用标题（无 `titleInputProps` 时） */
  title: string;
  titleInputProps?: React.ComponentProps<typeof TextArea>;
  content: unknown | null;
  editorKey?: string;
  onRichTextChange?: (value: unknown) => void;
  onRichTextBlur?: () => void;
  headerImageOverlay?: React.ReactNode;
  titleBandActions?: React.ReactNode;
  /** 插在标题区与正文区之间（如 `SelectMyImage`） */
  betweenTitleAndRichText?: React.ReactNode;
  richTextMinHeightClass?: string;
  richTextPlaceholder?: React.ReactNode;
  scrollable?: boolean;
  applyDocumentBackground?: boolean;
  /** 为 false 时标题带与正文容器不设主题纸面色（如列表缩略图铺满文档背景） */
  opaquePaperSurfaces?: boolean;
  /** 根容器额外 class（与 {@link innerClassName} 一并作用于同一 DOM，便于列表等场景只传其一） */
  rootClassName?: string;
  innerClassName?: string;
};

/**
 * 头图 + 标题 + Lexical 正文（不含中栏外壳与 DocToolBar）。
 * 列表缩略图等场景使用本组件；完整中栏用 {@link DocRichTextEditor}。
 */
export function DocRichTextDocument({
  documentBackground,
  headerImage,
  title,
  titleInputProps,
  content,
  editorKey,
  onRichTextChange,
  onRichTextBlur,
  headerImageOverlay,
  titleBandActions,
  betweenTitleAndRichText,
  richTextMinHeightClass = 'min-h-[400px]',
  richTextPlaceholder,
  scrollable = true,
  applyDocumentBackground = true,
  opaquePaperSurfaces = true,
  rootClassName,
  innerClassName,
}: DocRichTextDocumentProps) {
  const scrollClass = scrollable ? 'flex-1 overflow-y-auto overscroll-contain' : 'overflow-hidden';

  return (
    <div
      className={cn(
        'flex min-h-0 min-w-0 flex-col',
        scrollClass,
        rootClassName,
        innerClassName,
      )}
      style={applyDocumentBackground ? metaBackgroundToMiddleColumnStyle(documentBackground) : undefined}
    >
      <header className="flex shrink-0 flex-col overflow-visible rounded-t-2xl">
        {headerImage?.src ? (
          <div
            className="group/cover relative w-full shrink-0 overflow-hidden rounded-t-2xl"
            style={{ aspectRatio: String(KNOWLEDGE_BASE_HEADER_IMAGE_DISPLAY_ASPECT_RATIO) }}
          >
            <div className="absolute inset-0">
              {headerImage.cropNorm ? (
                <CroppedImageView src={headerImage.src} alt="" cropNorm={headerImage.cropNorm} />
              ) : (
                <img src={headerImage.src} alt="" className="absolute inset-0 size-full object-cover" />
              )}
            </div>
            {headerImageOverlay}
          </div>
        ) : null}
        <div
          className={cn(
            'group/addCover relative shrink-0 px-12 pt-8',
            opaquePaperSurfaces ? 'bg-surface' : 'bg-transparent',
            !headerImage?.src && 'rounded-t-2xl',
          )}
        >
          <div className="relative w-full min-w-0">
            {titleInputProps ? (
              <DocTitleTextArea {...titleInputProps} />
            ) : (
              <div
                className={cn(
                  DOC_TITLE_INPUT_CLASSNAME,
                  'cursor-default border-transparent select-none text-foreground',
                )}
              >
                {title.trim() || '未命名'}
              </div>
            )}
            {titleBandActions}
          </div>
        </div>
      </header>
      {betweenTitleAndRichText}
      <div
        className={cn(
          'flex shrink-0 flex-col rounded-b-2xl',
          opaquePaperSurfaces ? 'bg-surface' : 'bg-transparent',
          richTextMinHeightClass,
        )}
      >
        {richTextPlaceholder ? (
          richTextPlaceholder
        ) : (
          <RichTextEditor
            key={editorKey}
            growWithContent
            mode={onRichTextChange ? 'edit' : 'readonly'}
            value={content}
            onChange={onRichTextChange}
            onBlur={onRichTextBlur}
          />
        )}
      </div>
    </div>
  );
}

export type DocRichTextEditorMode = 'edit' | 'readonly';

export type DocRichTextEditorProps = {
  mode: DocRichTextEditorMode;
  /** 文档标题 */
  title: string;
  /** 知识库 `meta` JSON（含 background、headerImage 等） */
  meta: unknown;
  /** Lexical 序列化正文；空对象或 null 视为空文档 */
  content: unknown | null;
  /** 列表/记录上的历史头图 URL，供 `resolveKnowledgeBaseHeaderImageFromRecord` */
  coverImage?: string | null;
  editorKey?: string;
  richTextPlaceholder?: React.ReactNode;
  /** 外层布局（如编辑区 `flex-[3]`、预览页 `flex-1 min-h-0 w-full`） */
  shellClassName?: string;
  /** 编辑模式：背景变更 */
  onDocumentBackgroundChange?: (next: KnowledgeBaseMetaBackground) => void;
  onTitleChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  onTitleBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  selectHeaderImageOpen?: boolean;
  onSelectHeaderImageOpenChange?: (open: boolean) => void;
  onHeaderImagePick?: (pick: SelectMyImagePick) => void;
  onHeaderImageRemove?: () => void;
  onRichTextChange?: (value: unknown) => void;
  onRichTextBlur?: () => void;
};

/**
 * 知识库文档中栏：`edit` 含 DocToolBar 与交互；`readonly` 用于任意容器内的预览（无工具条与选图）。
 * 展示数据来自 `title`、`meta`、`content`（及可选 `coverImage`）。
 */
export function DocRichTextEditor({
  mode,
  title,
  meta,
  content,
  coverImage = null,
  editorKey,
  richTextPlaceholder,
  shellClassName,
  onDocumentBackgroundChange,
  onTitleChange,
  onTitleBlur,
  selectHeaderImageOpen = false,
  onSelectHeaderImageOpenChange,
  onHeaderImagePick,
  onHeaderImageRemove,
  onRichTextChange,
  onRichTextBlur,
}: DocRichTextEditorProps) {
  const isEdit = mode === 'edit';
  const navigate = useNavigate();

  const documentBackground = React.useMemo(() => parseKnowledgeBaseMetaBackgroundFromMeta(meta), [meta]);
  const headerImage = React.useMemo(
    () =>
      resolveKnowledgeBaseHeaderImageFromRecord(
        { meta, coverImage },
        KNOWLEDGE_BASE_HEADER_IMAGE_DISPLAY_ASPECT_RATIO,
      ),
    [meta, coverImage],
  );

  return (
    <div
      className={cn(
        'flex min-h-0 min-w-0 max-w-4xl mx-auto flex-col overflow-hidden rounded-lg border border-divider bg-surface px-8',
        shellClassName,
      )}
      style={metaBackgroundToMiddleColumnStyle(documentBackground)}
    >
      {isEdit ? (
        <header className="flex shrink-0 items-center gap-3 py-2" aria-label="文档顶栏">
          <Button
            type="button"
            variant="tertiary"
            isIconOnly
            size="sm"
            aria-label="返回上一页"
            className="shrink-0 text-foreground"
            onPress={() => navigate(-1)}
          >
            <IconChevronLeft className="size-5" stroke={1.5} />
          </Button>
          <div className="min-w-0 flex-1" aria-hidden />
          <DocToolBar
            documentBackground={documentBackground}
            onDocumentBackgroundChange={onDocumentBackgroundChange ?? (() => {})}
            readOnly={false}
          />
        </header>
      ) : null}
      <DocRichTextDocument
        scrollable
        applyDocumentBackground={false}
        documentBackground={documentBackground}
        headerImage={headerImage}
        title={title}
        titleInputProps={
          isEdit && onTitleChange
            ? {
                value: title,
                onChange: onTitleChange,
                onBlur: onTitleBlur,
                placeholder: '请输入标题',
                readOnly: false,
                maxLength: 255,
                'aria-label': '文档标题',
                className: DOC_TITLE_INPUT_CLASSNAME,
              }
            : undefined
        }
        headerImageOverlay={
          isEdit && onSelectHeaderImageOpenChange && onHeaderImageRemove ? (
            <div className="pointer-events-none absolute bottom-0 right-0 p-3 opacity-0 transition-opacity duration-200 group-hover/cover:pointer-events-auto group-hover/cover:opacity-100">
              <ButtonGroup className="overflow-hidden rounded-full border border-divider/60 bg-background/80 shadow-md backdrop-blur-sm">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="rounded-none px-3"
                  onPress={() => onSelectHeaderImageOpenChange(true)}
                >
                  更换头图
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="rounded-none px-3 text-danger"
                  onPress={onHeaderImageRemove}
                >
                  <ButtonGroup.Separator className="h-auto" />
                  移除
                </Button>
              </ButtonGroup>
            </div>
          ) : null
        }
        titleBandActions={
          isEdit && onSelectHeaderImageOpenChange && !headerImage?.src ? (
            <Button
              type="button"
              variant="ghost"
              className={cn(
                'absolute bottom-full left-0 z-10 h-6 px-2 text-xs',
                'pointer-events-none opacity-0 transition-opacity duration-150',
                'group-hover/addCover:pointer-events-auto group-hover/addCover:opacity-100',
                'group-focus-within/addCover:pointer-events-auto group-focus-within/addCover:opacity-100',
              )}
              onPress={() => onSelectHeaderImageOpenChange(true)}
            >
              <IconPhotoPlus stroke={1.25} />
              添加头图
            </Button>
          ) : null
        }
        betweenTitleAndRichText={
          isEdit && onHeaderImagePick && onSelectHeaderImageOpenChange ? (
            <SelectMyImage
              open={selectHeaderImageOpen}
              onOpenChange={onSelectHeaderImageOpenChange}
              cropAspectRatio={KNOWLEDGE_BASE_HEADER_IMAGE_DISPLAY_ASPECT_RATIO}
              onPick={onHeaderImagePick}
            />
          ) : null
        }
        richTextPlaceholder={richTextPlaceholder}
        editorKey={editorKey}
        content={content}
        onRichTextChange={isEdit ? onRichTextChange : undefined}
        onRichTextBlur={isEdit ? onRichTextBlur : undefined}
      />
    </div>
  );
}
